import { LitElement, css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators';

import { GetActivatedChallengeResponse, TranslationPhrases } from '../types';
import challengeData from '../helpers/state/challenge-data';
import translationData from '../helpers/state/translation-data';
import { undefinedIfEmpty } from '../helpers/utils';

/**
 * Challenge Leaderboard Component
 *
 * Reusable component for displaying challenge leaderboards
 */
@customElement('challenge-leaderboard')
export class ChallengeLeaderboard extends LitElement {
    @property({ type: Array })
    participants: GetActivatedChallengeResponse[] = [];

    @property({ type: Boolean })
    loading: boolean = false;

    @property({ type: String })
    errorMessage?: string;

    @state()
    userPosition?: number | 'hidden';

    @state()
    leaderboardTab: 'participants' | 'places' = 'participants';

    @state()
    private translatedLabels: TranslationPhrases = {};

    // Tab options and the visit count are props/interpolations, so they can't use <translated-span>.
    private get labelPhrases(): TranslationPhrases {
        return {
            participantLeaderboard: 'Participant Leaderboard',
            placesLeaderboard: 'Places Leaderboard',
            visit: 'Visit',
            visits: 'Visits',
        };
    }

    private async translateLabels() {
        this.translatedLabels = await translationData.translateFields(this.labelPhrases);
    }

    async firstUpdated() {
        translationData.onLanguageChanged(() => {
            void this.translateLabels();
        });
        await this.translateLabels();
    }

    private changeLeaderboardType(e: CustomEvent | any) {
        if (e.detail.tabIndex === 0) {
            this.leaderboardTab = 'participants';
        } else {
            this.leaderboardTab = 'places';
        }
        this.requestUpdate();
    }

    render() {
        if (this.loading) {
            return html`
                <div class="loading">
                    <p><translated-span>Loading leaderboard...</translated-span></p>
                </div>
            `;
        }

        if (this.errorMessage) {
            return html`
                <div class="error">
                    <p>${this.errorMessage}</p>
                </div>
            `;
        }

        return html`
            <tabs-component id="leaderboardTabs"
                .options=${[this.translatedLabels.participantLeaderboard ?? 'Participant Leaderboard', this.translatedLabels.placesLeaderboard ?? 'Places Leaderboard']}
                .type=${'pill'}
                style="width: 100%;margin-bottom: 1rem;"
                tabIndex="0"
                @on-select-tab=${(e: CustomEvent) => {
                    this.changeLeaderboardType(e);
                }}
            ></tabs-component>
            ${this.leaderboardTab === 'participants'
                ? html`
                    ${this.userPosition !== undefined ? html`
                        <div class="user_position">
                            <div class="challenge-leaders__row active">
                                <div class="challenge-leaders__user-name">
                                    <b><translated-span>Your ranking</translated-span></b>
                                    ${this.userPosition === 'hidden' ? html`
                                        <span class="user_position__hidden-note"><translated-span>You're hidden from the public leaderboard.</translated-span></span>
                                    ` : ''}
                                </div>
                                <div class="challenge-leaders__point">
                                    ${this.userPosition === 'hidden' ? html`<span><translated-span>Hidden</translated-span></span>` : html`#${this.userPosition}`}
                                </div>
                            </div>
                        </div>
                    ` : ''}
                    <h2><translated-span>Leaderboard</translated-span></h2>
                    <div class="challenge-leaders__container">
                        ${[...(this.participants ?? [])]
                            .filter(activatedChallenge => activatedChallenge.point_count !== undefined && activatedChallenge.point_count > 0 && !activatedChallenge.hidden_from_leaderboard)
                            .sort((a, b) => (b.point_count ?? 0) - (a.point_count ?? 0))
                            .map((activatedChallenge) => html`
                        <div class="challenge-leaders__row ${challengeData.activated_challenge?._id.$oid === activatedChallenge._id.$oid ? 'active' : ''}">
                            <div class="challenge-leaders__content">
                                <div class="challenge-leaders__user-name">
                                    ${undefinedIfEmpty(activatedChallenge.display_name) ? html`<b>${activatedChallenge.display_name}</b>` : html`<b><translated-span>Anonymous Participant</translated-span></b>`}
                                    <span>${activatedChallenge.point_count ?? 0} <translated-span>Points</translated-span> | ${activatedChallenge.checkin_count ?? 0} <translated-span>Places Visited</translated-span></span>

                                </div>
                            </div>
                            <div class="challenge-leaders__point">
                                ${activatedChallenge.earned_rewards?.map((reward) => html`
                                    <img src="${reward.image_url}" class="award-icon" alt="${reward.title}"/>
                                `)}
                            </div>
                        </div>
                    `)}
                `: html`
                    <h2><translated-span>Places Leaderboard</translated-span></h2>
                    <div class="challenge-leaders__container">
                        ${(() => {
                            // Create a map from location ID to location object for O(1) lookup
                            const locationMap = new Map(
                                challengeData.challengeLocations.map(loc => [
                                    loc.challenge_location_info?._id?.$oid,
                                    loc
                                ])
                            );
                            // Drop places whose underlying location was removed from
                            // the challenge — the check-in history lingers server-side
                            // but rendering them as "Unknown Place" is confusing.
                            const visiblePlaces = challengeData.placesLeaderboard
                                .filter(place => locationMap.has(place.id)
                                    && locationMap.get(place.id)?.challenge_location_info?.hide_from_leaderboard !== true);
                            if (visiblePlaces.length === 0) {
                                return html`<p class="no-data">No place visits yet.</p>`;
                            }
                            return visiblePlaces.map((place) => {
                                const location = locationMap.get(place.id);
                                return html`
                                <div class="challenge-leaders__row">
                                    <div class="challenge-leaders__content">
                                        <div class="challenge-leaders__user-name">
                                            <b>${location?.point_info?.name ?? ''}</b>
                                        </div>
                                    </div>
                                    <div class="challenge-leaders__point">
                                        ${place.count ?? 0} ${place.count === 1 ? this.translatedLabels.visit ?? 'Visit' : this.translatedLabels.visits ?? 'Visits'}
                                    </div>
                                </div>
                                `;
                            });
                        })()}
                    </div>
                `}

        `;
    }

    static styles = css`
        :host {
            display: block;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
        }

        .user_position {
            margin-bottom: 1rem;
            margin-top: 1rem;
        }

        .user_position__hidden-note {
            font-size: 0.85rem;
            opacity: 0.8;
            margin-top: 2px;
        }

        .challenge-leaders__container {
            display: flex;
            flex-direction: column;
            overflow-y: auto;
        }


        .challenge-leaders__content {
            display: flex;
            align-items: center;
        }

        .challenge-leaders__user-name {
            display: flex;
            align-items: center;
            flex-direction: column;
            justify-content: center;
            align-items: flex-start;
        }

        .award-icon {
            width: 20px;
            height: 20px;
            margin-right: -2px;
            border: 1px solid var(--theme-border-color);
            border-radius: 50%;
        }

        .challenge-leaders__places {
            margin-left: 10px;
            color: #777;
        }

        .challenge-leaders__point {
            font-weight: bold;
            color: #444;
        }

        #rewards {
            overflow-y: auto;
        }


        .challenge-leaders__row.active {
            border: 1px solid var(--main-featured-color);
            border-radius: 4px;
            background-color: var(--main-featured-color);
        }

        .challenge-leaders__row {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 10px;
            transition: background-color 0.3s ease;
        }

        .loading,
        .error {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            min-height: 300px;
            padding: 2rem;
        }

                    .challenge-leaders__point {
                font-weight: bold;
                color: #444;
            }

            #rewards {
                overflow-y: auto;
            }

        .spinner {
            width: 50px;
            height: 50px;
            border: 4px solid #f3f3f3;
            border-top: 4px solid #3498db;
            border-radius: 50%;
            animation: spin 1s linear infinite;
        }

        @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
        }

        .error {
            color: #e74c3c;
        }

        .leaderboard-container {
            display: flex;
            flex-direction: column;
        }

        .leaderboard-header {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            padding: 2rem;
            text-align: center;
            border-radius: 8px 8px 0 0;
        }

        .leaderboard-header h2 {
            margin: 0;
            font-size: 2rem;
            font-weight: 600;
        }

        .leaderboard-content {
            padding: 2rem;
            background: #ffffff;
        }

        .no-data {
            text-align: center;
            padding: 3rem;
            color: #999;
            font-size: 1.1rem;
        }

        .leaderboard-table {
            background: white;
            border-radius: 8px;
            overflow: hidden;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }

        .table-header {
            display: grid;
            grid-template-columns: 80px 1fr 120px 120px;
            gap: 1rem;
            padding: 1rem;
            background: #f8f9fa;
            font-weight: 600;
            color: #333;
            border-bottom: 2px solid #e0e0e0;
        }

        .table-row {
            display: grid;
            grid-template-columns: 80px 1fr 120px 120px;
            gap: 1rem;
            padding: 1rem;
            border-bottom: 1px solid #f0f0f0;
            transition: background-color 0.2s;
        }

        .table-row:hover {
            background-color: #f8f9fa;
        }

        .table-row:last-child {
            border-bottom: none;
        }

        .rank-cell {
            font-weight: 600;
            color: #667eea;
        }

        .name-cell {
            font-weight: 500;
            color: #333;
        }

        .checkins-cell,
        .points-cell {
            text-align: right;
            color: #666;
        }

        @media (max-width: 768px) {
            .table-header,
            .table-row {
                grid-template-columns: 60px 1fr 80px 80px;
                gap: 0.5rem;
                padding: 0.75rem;
                font-size: 0.9rem;
            }

            .leaderboard-header h2 {
                font-size: 1.5rem;
            }

            .leaderboard-content {
                padding: 1rem;
            }
        }
    `;
}
